import React from 'react';
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
    Download,
} from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import { UARMrDetailRow } from '../api';

interface Props {
    data: UARMrDetailRow[];
    loading: boolean;
    customerName: string;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: 'reference_no' | 'utr_no' | 'utr_date' | 'utr_amt' | 'mr_date' | 'overdue_days' | 'transaction_amt' | 'mr_amount';
    sortOrder: 'asc' | 'desc';
    onSortChange: (col: 'reference_no' | 'utr_no' | 'utr_date' | 'utr_amt' | 'mr_date' | 'overdue_days' | 'transaction_amt' | 'mr_amount') => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onBack: () => void;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onConversionChange: (val: 'INR' | 'Lakhs' | 'Crores') => void;
    onDownloadExcel: () => void;
    downloadingExcel: boolean;
}

const columns: Array<{
    key: Props['sortBy'];
    label: string;
    isNumeric: boolean;
}> = [
        { key: 'reference_no', label: 'Reference No', isNumeric: false },
        { key: 'utr_no', label: 'UTR No', isNumeric: false },
        { key: 'utr_date', label: 'UTR Date', isNumeric: false },
        { key: 'utr_amt', label: 'UTR Amount', isNumeric: true },
        { key: 'mr_date', label: 'MR Date', isNumeric: false },
        { key: 'overdue_days', label: 'Overdue Days', isNumeric: true },
        { key: 'transaction_amt', label: 'Transaction Amount', isNumeric: true },
        { key: 'mr_amount', label: 'UAR Amount', isNumeric: true },
    ];

const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

const formatNumber = (value: number, conversion: 'INR' | 'Lakhs' | 'Crores') => {
    let displayValue = value;
    if (conversion === 'Lakhs') displayValue = value / 100000;
    if (conversion === 'Crores') displayValue = value / 10000000;

    return displayValue.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const UarMrDetailsTable: React.FC<Props> = ({
    data,
    loading,
    customerName,
    page,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    onBack,
    conversionType,
    onConversionChange,
    onDownloadExcel,
    downloadingExcel,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    const { isLoading, showContent } = useSmoothLoading(loading, {
        minLoadingTime: 150,
        debounceTime: 150,
    });

    const renderLoadingRows = () =>
        Array.from({ length: 10 }).map((_, i) => (
            <tr key={i}>
                {columns.map((_, j) => (
                    <td key={j} className="border px-2 py-1.5">
                        <Skeleton className="h-4 w-full" />
                    </td>
                ))}
            </tr>
        ));

    const opacityClass = isLoading || showContent ? 'opacity-100' : 'opacity-0';

    return (
        <div className={`flex flex-col h-full min-h-0 transition-opacity duration-300 ${opacityClass}`}>
            <div className="flex items-center gap-2 pb-4">
                <button
                    onClick={onBack}
                    className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300"
                >
                    ← Back
                </button>
                <span className="text-lg ml-2 font-semibold">{customerName}</span>

                <div className="ml-auto flex items-center gap-2">
                    {(['INR', 'Lakhs', 'Crores'] as const).map((t) => (
                        <button
                            key={t}
                            onClick={() => onConversionChange(t)}
                            className={`px-3 py-1 text-sm rounded-md ${conversionType === t
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                }`}
                        >
                            {t}
                        </button>
                    ))}

                    <button
                        onClick={onDownloadExcel}
                        disabled={downloadingExcel}
                        className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 flex items-center gap-1"
                    >
                        <Download size={16} />
                        {downloadingExcel ? 'Downloading...' : 'Excel'}
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-auto border border-gray-200">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((col) => {
                                const isActive = sortBy === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => onSortChange(col.key)}
                                        className={`border px-2 py-2 font-bold cursor-pointer ${
                                            col.isNumeric ? 'text-right' : 'text-left'
                                        }`}
                                    >
                                        <div className="flex items-center gap-1">
                                            {col.label}
                                            {isActive &&
                                                (sortOrder === 'asc' ? (
                                                    <ChevronUp size={14} />
                                                ) : (
                                                    <ChevronDown size={14} />
                                                ))}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            renderLoadingRows()
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="py-20 text-center text-gray-500">
                                    No MR details found.
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => (
                                <tr key={idx}>
                                    <td className="border px-2 py-1.5">{row.reference_no}</td>
                                    <td className="border px-2 py-1.5">{row.utr_no || '-'}</td>
                                    <td className="border px-2 py-1.5">{formatDate(row.utr_date)}</td>
                                    <td className="border px-2 py-1.5 text-right">{formatNumber(row.utr_amt, conversionType)}</td>
                                    <td className="border px-2 py-1.5">{formatDate(row.mr_date)}</td>
                                    <td className="border px-2 py-1.5 text-right">{row.overdue_days}</td>
                                    <td className="border px-2 py-1.5 text-right">
                                        {formatNumber(row.transaction_amt, conversionType)}
                                    </td>
                                    <td className="border px-2 py-1.5 text-right">
                                        {formatNumber(row.mr_amount, conversionType)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between shrink-0 pt-4">
                <span className="text-sm text-gray-600">
                    Showing{' '}
                    <span className="font-semibold">
                        {totalItems === 0 ? 0 : (page - 1) * pageSize + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-semibold">
                        {Math.min(page * pageSize, totalItems)}
                    </span>{' '}
                    of <span className="font-semibold">{totalItems}</span> results
                </span>

                <div className="flex items-center gap-3 text-sm space-x-10">
                    <div className="flex items-center gap-1">
                        <span className="text-gray-600 mr-1">Rows per page</span>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="border border-gray-300 rounded px-2 py-1"
                        >
                            {[10, 20, 50, 100].map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <span className="text-gray-600">
                        Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                    </span>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(1)}
                            className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronsLeft size={16} />
                        </button>

                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(page - 1)}
                            className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronLeft size={16} />
                        </button>

                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(page + 1)}
                            className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronRight size={16} />
                        </button>

                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(totalPages)}
                            className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UarMrDetailsTable;
